const React = require("react");
const PropTypes = require("prop-types");

function getDisplayItems(items, limit) {
  const sourceItems = Array.isArray(items) ? items : [];
  const normalizedLimit =
    Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : sourceItems.length;

  return sourceItems
    .filter((item) => item && item.id && item.permalink && (item.thumbnailUrl || item.mediaUrl))
    .slice(0, normalizedLimit);
}

function getAltText(item) {
  if (item && item.caption) {
    return item.caption.length > 160
      ? `${item.caption.slice(0, 157)}...`
      : item.caption;
  }

  const mediaType = item && item.mediaType ? item.mediaType.toLowerCase().replace(/_/g, " ") : "post";
  return `Instagram ${mediaType} from Browned & Toasted`;
}

function getPreviewImageUrl(item) {
  if (!item) {
    return null;
  }

  return item.thumbnailUrl || item.mediaUrl || null;
}

function getBadgeLabel(mediaType) {
  if (mediaType === "VIDEO") {
    return "Video";
  }

  if (mediaType === "CAROUSEL_ALBUM") {
    return "Carousel";
  }

  return null;
}

function getCaptionSnippet(caption) {
  if (!caption) {
    return "";
  }

  return caption.length > 96 ? `${caption.slice(0, 93)}...` : caption;
}

function InstagramFeed({
  ctaHref,
  ctaLabel = "Follow us on Instagram",
  emptyMessage = "No recent Instagram posts are available right now.",
  heading,
  items,
  limit,
}) {
  const displayItems = getDisplayItems(items, limit);

  return React.createElement(
    "section",
    {
      className:
        "container my-16 rounded-[2rem] border border-[#e0b15e]/35 bg-[linear-gradient(180deg,rgba(255,250,235,0.95),rgba(255,255,255,0.98))] px-5 py-8 shadow-sm sm:px-8 sm:py-10",
    },
    React.createElement(
      "div",
      {
        className:
          "mb-8 flex flex-col gap-4 border-b border-[#e0b15e]/25 pb-6 sm:flex-row sm:items-end sm:justify-between",
      },
      React.createElement(
        "div",
        { className: "max-w-2xl" },
        heading
          ? React.createElement(
              "h2",
              {
                className:
                  "font-[Staatliches] text-3xl tracking-[0.08em] text-chocolate sm:text-4xl",
              },
              heading
            )
          : null,
        React.createElement(
          "p",
          { className: "mt-3 text-sm font-medium text-[#603026]/80 sm:text-base" },
          "Fresh treats, pop-ups, and behind-the-scenes moments from the kitchen."
        )
      ),
      ctaHref
        ? React.createElement(
            "a",
            {
              className:
                "inline-flex items-center justify-center rounded-full bg-crimson px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:brightness-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-crimson/40 focus-visible:ring-offset-2",
              href: ctaHref,
              rel: "noopener noreferrer",
              target: "_blank",
            },
            ctaLabel
          )
        : null
    ),
    displayItems.length
      ? React.createElement(
          "ul",
          { className: "grid grid-cols-2 gap-4 md:grid-cols-3" },
          displayItems.map((item) => {
            const badgeLabel = getBadgeLabel(item.mediaType);
            const previewImageUrl = getPreviewImageUrl(item);

            return React.createElement(
              "li",
              { className: "list-none", key: item.id },
              React.createElement(
                "a",
                {
                  className:
                    "group block overflow-hidden rounded-[1.5rem] border border-[#603026]/10 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-crimson/30 focus-visible:ring-offset-2",
                  href: item.permalink,
                  rel: "noopener noreferrer",
                  target: "_blank",
                },
                React.createElement(
                  "div",
                  { className: "relative aspect-square overflow-hidden bg-[#fffaeb]" },
                  previewImageUrl
                    ? React.createElement("img", {
                        alt: getAltText(item),
                        className:
                          "absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]",
                        loading: "lazy",
                        src: previewImageUrl,
                      })
                    : null,
                  badgeLabel
                    ? React.createElement(
                        "span",
                        {
                          className:
                            "absolute left-3 top-3 rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white",
                        },
                        badgeLabel
                      )
                    : null
                ),
                React.createElement(
                  "div",
                  { className: "min-h-[72px] px-4 py-3" },
                  React.createElement(
                    "p",
                    {
                      className: "text-sm font-semibold leading-5 text-chocolate",
                      style: {
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        display: "-webkit-box",
                        overflow: "hidden",
                      },
                    },
                    getCaptionSnippet(item.caption) || "View this Instagram post"
                  )
                )
              )
            );
          })
        )
      : React.createElement(
          "div",
          {
            className:
              "rounded-[1.5rem] border border-dashed border-[#603026]/20 bg-white/70 px-6 py-10 text-center text-sm font-medium text-[#603026]/75",
          },
          emptyMessage
        )
  );
}

InstagramFeed.propTypes = {
  ctaHref: PropTypes.string,
  ctaLabel: PropTypes.string,
  emptyMessage: PropTypes.string,
  heading: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      caption: PropTypes.string,
      id: PropTypes.string.isRequired,
      mediaType: PropTypes.string,
      mediaUrl: PropTypes.string,
      permalink: PropTypes.string.isRequired,
      thumbnailUrl: PropTypes.string,
    })
  ),
  limit: PropTypes.number,
};

module.exports = InstagramFeed;
module.exports.getAltText = getAltText;
module.exports.getDisplayItems = getDisplayItems;
